import { useState } from 'react';
import { useTranslation } from '../../i18n/LocaleProvider';
import type { DsTranslationKey } from '../../i18n/catalogues/en';
import { EChart as ReactEChartsCore } from './EChart';
import * as echarts from 'echarts/core';
import { BarChart as EChartsBar } from 'echarts/charts';
import { AriaComponent, GridComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

import { PHASE_BUDGET } from '../../data/fixtures';
import { formatMoney, formatNumber } from '../../i18n/format';
import { ChartFrame } from './ChartFrame';
import { ChartLegend, type ChartLegendItem } from './ChartLegend';
import {
  axisTokens,
  entranceAnimation,
  seriesColor,
  tooltipTokens,
  useChartMotion,
  useChartTheme,
} from './ChartTheme';

echarts.use([EChartsBar, AriaComponent, GridComponent, TooltipComponent, CanvasRenderer]);

const SERIES: Array<{ id: string; labelKey: DsTranslationKey; field: 'budgeted' | 'actual' }> = [
  { id: 'budgeted', labelKey: 'ds.chart.series.budgeted', field: 'budgeted' },
  { id: 'actual', labelKey: 'ds.chart.series.actual', field: 'actual' },
];

const money = (value: number) => formatMoney(value, { precision: 0 });

export function BarChart() {
  const { t } = useTranslation();
  const theme = useChartTheme();
  const motion = useChartMotion(theme);
  const [hidden, setHidden] = useState<string[]>([]);

  const axis = axisTokens(theme);

  /*
     Both names come through the catalogue rather than off the row, so the axis
     and the data table are in the reader's language. The axis carries the short
     phase name so eight bars fit; the data table below carries the full one.
  */
  const phases = PHASE_BUDGET.map((row) => t(`ds.phase.${row.phaseId}.short`));

  const legendItems: ChartLegendItem[] = SERIES.map((series, index) => ({
    id: series.id,
    label: t(series.labelKey),
    color: seriesColor(theme, index),
    value: money(PHASE_BUDGET.reduce((total, row) => total + row[series.field], 0)),
  }));

  const series = SERIES.filter((entry) => !hidden.includes(entry.id)).map((entry) => ({
    id: entry.id,
    name: t(entry.labelKey),
    type: 'bar',
    itemStyle: { color: seriesColor(theme, SERIES.findIndex((s) => s.id === entry.id)) },
    data: PHASE_BUDGET.map((row) => row[entry.field]),
    animationDelay: motion.delay,
  }));

  const budgeted = PHASE_BUDGET.reduce((total, row) => total + row.budgeted, 0);
  const actual = PHASE_BUDGET.reduce((total, row) => total + row.actual, 0);

  return (
    <ChartFrame
      title={t('ds.chart.bar.title')}
      description={t('ds.chart.bar.description')}
      summary={t('ds.chart.bar.summary', {
        phases: formatNumber(PHASE_BUDGET.length),
        budgeted: money(budgeted),
        actual: money(actual),
      })}
      chartLabel={t('ds.chart.bar.chartLabel', { phases: formatNumber(PHASE_BUDGET.length) })}
      legend={<ChartLegend label={t('ds.chart.bar.legend')} items={legendItems} hidden={hidden} onToggle={(id) => setHidden((prev) => (prev.includes(id) ? prev.filter((entry) => entry !== id) : [...prev, id]))} />}
      empty={series.length === 0 ? t('ds.chart.bar.empty') : undefined}
      table={{
        caption: t('ds.chart.bar.tableCaption'),
        columns: [t('ds.chart.bar.columnPhase'), t('ds.chart.series.budgeted'), t('ds.chart.series.actual')],
        rows: PHASE_BUDGET.map((row) => [
          t(`ds.phase.${row.phaseId}`),
          money(row.budgeted),
          money(row.actual),
        ]),
      }}
    >
      <ReactEChartsCore
        echarts={echarts}
        style={{ width: '100%', height: '100%' }}
        option={{
          ...entranceAnimation(motion),
          aria: { enabled: true, label: { enabled: false }, decal: { show: true } },
          grid: { left: 4, right: 16, top: 16, bottom: 4, containLabel: true },
          tooltip: { trigger: 'axis', ...tooltipTokens(theme, money), axisPointer: { type: 'shadow' } },
          xAxis: { type: 'category', data: phases, ...axis, splitLine: { show: false } },
          yAxis: { type: 'value', ...axis, axisLabel: { ...axis.axisLabel, formatter: money } },
          series,
        }}
      />
    </ChartFrame>
  );
}
